import { type CSSProperties, type ReactNode, useState } from 'react';
import { formatDateTime } from '../../../lib/time';
import { formatInt, formatSeconds, formatUsd } from '../../charts/AiUsageBlocks';
import { Icon } from '../../icons';
import { copyToClipboard } from '../../shared';
import { BottomSheet } from '../BottomSheet';
import type { MobileShellCtx } from '../MobileShellCtx';

const LABEL: CSSProperties = {
  fontSize: 10.5,
  letterSpacing: '0.16em',
  textTransform: 'uppercase',
  color: 'var(--fg-3)',
  fontWeight: 500,
  marginBottom: 8,
};

function highlightJson(value: unknown): ReactNode {
  const json = JSON.stringify(value, null, 2);
  if (!json) return null;
  const parts: ReactNode[] = [];
  const regex =
    /("(?:\\.|[^"\\])*"\s*:)|("(?:\\.|[^"\\])*")|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)|(true|false)|(null)/g;
  let last = 0;
  let key = 0;
  let m: RegExpExecArray | null = regex.exec(json);
  while (m !== null) {
    if (m.index > last) parts.push(json.slice(last, m.index));
    const cls = m[1]
      ? 'json-key'
      : m[2]
        ? 'json-str'
        : m[3]
          ? 'json-num'
          : m[4]
            ? 'json-bool'
            : 'json-null';
    parts.push(
      <span key={key++} className={cls}>
        {m[0]}
      </span>,
    );
    last = regex.lastIndex;
    m = regex.exec(json);
  }
  if (last < json.length) parts.push(json.slice(last));
  return parts;
}

export function AIEventSheet({ ctx, eventIdx }: { ctx: MobileShellCtx; eventIdx: number }) {
  const [copied, setCopied] = useState(false);
  const event =
    typeof window !== 'undefined' && window.__mobileAiEvents
      ? window.__mobileAiEvents[eventIdx]
      : null;

  const unit: 'tokens' | 'audio' | 'chars' | null = !event
    ? null
    : event.inputTokens + event.outputTokens > 0
      ? 'tokens'
      : event.audioSeconds > 0
        ? 'audio'
        : event.characters > 0
          ? 'chars'
          : null;
  const unitLabel = unit === 'tokens' ? 'Tokens' : unit === 'audio' ? 'Audio' : 'Chars';
  const hasMetadata = event && Object.keys(event.metadata).length > 0;

  return (
    <BottomSheet
      title="Evento AI"
      onClose={ctx.closeSheet}
      action={
        event ? (
          <button
            type="button"
            className="sheet-close"
            aria-label="Copy event"
            title={copied ? 'Copiado' : 'Copiar JSON'}
            onClick={() =>
              copyToClipboard(JSON.stringify(event, null, 2), () => {
                setCopied(true);
                window.setTimeout(() => setCopied(false), 1500);
              })
            }
          >
            {copied ? <Icon.Check size={13} /> : <Icon.Copy size={13} />}
          </button>
        ) : undefined
      }
    >
      {!event && <div className="m-empty">Evento no disponible</div>}
      {event && (
        <>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
            <span
              className="swatch"
              style={{ width: 12, height: 12, borderRadius: 4, background: 'var(--accent)' }}
            />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 14, fontWeight: 500, wordBreak: 'break-word' }}>
                {event.model}
              </div>
              <div
                className="mono"
                style={{
                  fontSize: 10.5,
                  color: 'var(--fg-4)',
                  letterSpacing: '0.1em',
                  textTransform: 'uppercase',
                }}
              >
                {event.provider}
              </div>
            </div>
            <span
              className="mono"
              style={{ fontSize: 11, color: 'var(--fg-3)', marginLeft: 'auto' }}
            >
              {formatDateTime(event.timestamp)}
            </span>
          </div>

          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 14 }}
          >
            <div className="kpi-tile" style={{ padding: 12 }}>
              <div className="l">
                <Icon.Coin size={11} />
                Coste
              </div>
              <div className="v mono">{formatUsd(event.costUsd ?? 0)}</div>
              <div className="d">{event.costSource}</div>
            </div>
            {unit && (
              <div className="kpi-tile" style={{ padding: 12 }}>
                <div className="l">
                  <Icon.Bolt size={11} />
                  Unidad
                </div>
                <div className="v" style={{ display: 'inline-flex', alignItems: 'center' }}>
                  <span className={`unit-chip ${unit}`}>{unitLabel}</span>
                </div>
              </div>
            )}
          </div>

          <div style={LABEL}>Unidades facturables</div>
          <div className="kv-block" style={{ marginBottom: 14 }}>
            {unit === 'tokens' && (
              <>
                <div className="kv-row">
                  <span className="k">Input tokens</span>
                  <span className="v">{formatInt(event.inputTokens)}</span>
                </div>
                <div className="kv-row">
                  <span className="k">Output tokens</span>
                  <span className="v">{formatInt(event.outputTokens)}</span>
                </div>
                {event.cachedInputTokens > 0 && (
                  <div className="kv-row">
                    <span className="k">Cached input</span>
                    <span className="v">{formatInt(event.cachedInputTokens)}</span>
                  </div>
                )}
              </>
            )}
            {unit === 'audio' && (
              <div className="kv-row">
                <span className="k">Audio</span>
                <span className="v">{formatSeconds(event.audioSeconds)}</span>
              </div>
            )}
            {unit === 'chars' && (
              <div className="kv-row">
                <span className="k">Caracteres</span>
                <span className="v">{formatInt(event.characters)}</span>
              </div>
            )}
            {!unit && (
              <div className="kv-row">
                <span className="k">Unidades</span>
                <span className="v">—</span>
              </div>
            )}
          </div>

          <div style={LABEL}>Contexto</div>
          <div className="kv-block" style={{ marginBottom: hasMetadata ? 14 : 0 }}>
            <div className="kv-row">
              <span className="k">App</span>
              <span className="v">{event.applicationName}</span>
            </div>
            <div className="kv-row">
              <span className="k">Provider</span>
              <span className="v">{event.provider}</span>
            </div>
            <div className="kv-row">
              <span className="k">Model</span>
              <span className="v">{event.model}</span>
            </div>
            <div className="kv-row">
              <span className="k">Timestamp</span>
              <span className="v">{formatDateTime(event.timestamp)}</span>
            </div>
          </div>

          {hasMetadata && (
            <>
              <div style={LABEL}>Metadata</div>
              <pre className="json-block">{highlightJson(event.metadata)}</pre>
            </>
          )}
        </>
      )}
    </BottomSheet>
  );
}
